import { useRef } from 'react';

import { BpkOverlayView } from '@skyscanner-internal/backpack-web/bpk-component-map';
import { redirect } from 'saddlebag-browser';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import MapTooltip from '../MapTooltip';
import DotMarker from '../Markers/DotMarker';
import PriceMarker from '../Markers/PriceMarker';
import PriceMarkerV2 from '../Markers/PriceMarkerV2';
import ProviderCard from '../Popups/DotMarkerPopup';
import HotelMarkerPopup from '../Popups/HotelMarkerPopup';
import PriceMarkerPopup from '../Popups/PriceMarkerPopup';
import MARKERS_DATA_SOURCE from '../types';

import type {
  DynamicMapStrings,
  MarkersDataSource,
  MapMarker,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DynamicMapProps';

import STYLES from './MapDesktopMarker.module.scss';

type MapDesktopMarkerProps = {
  index: number;
  location: MapMarker;
  strings: DynamicMapStrings;
  priceMarkerVersion?: string;
};

const getPlacement = (markersDataSource: MarkersDataSource) => {
  switch (markersDataSource) {
    case MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_WITH_PRICE_FROM_DEAL:
      return 'bottom';
    case MARKERS_DATA_SOURCE.HOTELS_DEAL_INFORMATION_FROM_DEAL:
      return 'auto';
    default:
      return 'left';
  }
};

const onClickMarker = (redirectUrl: string) => {
  redirect(redirectUrl);
};

const getMarker = (
  markersDataSource: MarkersDataSource,
  location: MapMarker,
  priceMarkerVersion: string = 'v2',
) => {
  if (
    markersDataSource ===
    MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_WITH_PRICE_FROM_DEAL
  ) {
    return (
      <PriceMarker
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => onClickMarker(location.redirectUrl ?? '')}
      />
    );
  }
  if (
    markersDataSource === MARKERS_DATA_SOURCE.HOTELS_DEAL_INFORMATION_FROM_DEAL
  ) {
    return priceMarkerVersion === 'v2' ? (
      <PriceMarkerV2
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => onClickMarker(location.redirectUrl ?? '')}
      />
    ) : (
      <PriceMarker
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => onClickMarker(location.redirectUrl ?? '')}
      />
    );
  }
  return <DotMarker />;
};

const getMarkerPopup = (
  markersDataSource: MarkersDataSource,
  location: MapMarker,
  strings: DynamicMapStrings,
  index: number,
) => {
  switch (markersDataSource) {
    case MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_WITH_PRICE_FROM_DEAL:
      return (
        <div className={STYLES.MapDesktopMarker__priceMarkerPopupWrapper}>
          <PriceMarkerPopup
            index={index}
            redirectUrl={location.redirectUrl ?? ''}
            address={location.address ?? ''}
            logos={location.logos}
            strings={strings}
            popupLinkStr={location.popupLinkStr ?? ''}
          />
        </div>
      );
    case MARKERS_DATA_SOURCE.HOTELS_DEAL_INFORMATION_FROM_DEAL:
      if (
        !location.hotelImageAltText ||
        !location.price ||
        !location.priceTrailingText
      ) {
        return null;
      }
      return (
        <div className={STYLES.MapDesktopMarker__hotelMarkerPopupWrapper}>
          <HotelMarkerPopup
            index={index}
            hotelName={location.entityName}
            redirectUrl={location.redirectUrl ?? ''}
            price={location.price}
            priceTrailingText={location.priceTrailingText}
            hotelImageAltText={location.hotelImageAltText}
            hotelImageUrl={location.hotelImageUrl}
            hotelReviewsData={location.hotelReviewsData}
            hotelRatingData={location.hotelRatingData}
          />
        </div>
      );
    default:
      return (
        <div className={STYLES.MapDesktopMarker__cardWrapper}>
          <ProviderCard
            index={index}
            redirectUrl={location.redirectUrl ?? ''}
            entityName={location.entityName}
            logos={location.logos}
            markersDataSource={markersDataSource}
            address={location.address ?? ''}
            strings={strings}
          />
        </div>
      );
  }
};

const MapDesktopMarker = ({
  index,
  location,
  priceMarkerVersion = 'v2',
  strings,
}: MapDesktopMarkerProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  const target = useRef(null);
  const renderTargetId = `renderTargetId-${index}`;

  const position = {
    latitude: location.latitude,
    longitude: location.longitude,
  };

  return (
    <BpkOverlayView position={position}>
      <div
        className={STYLES.MapDesktopMarker__tooltipContent}
        id={renderTargetId}
      >
        <MapTooltip
          target={
            <span ref={target}>
              <div
                className={STYLES.MapDesktopMarker__clusterContainer}
                data-tracking-element-id={`${rawComponentName}_marker_${index}`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_marker`,
                )}
              >
                {getMarker(
                  location.markersDataSource,
                  location,
                  priceMarkerVersion,
                )}
              </div>
            </span>
          }
          portalClassName={STYLES.MapDesktopMarker__tooltip}
          renderTarget={() => document.getElementById(renderTargetId)}
          placement={getPlacement(location.markersDataSource)}
        >
          <div
            data-testid="mapMarkerPopup"
            data-tracking-element-id={`${rawComponentName}_popup_${index}`}
            data-tracking-common-params={buildMBDParam(
              `${rawComponentName}_popup`,
            )}
          >
            {getMarkerPopup(
              location.markersDataSource,
              location,
              strings,
              index,
            )}
          </div>
        </MapTooltip>
      </div>
    </BpkOverlayView>
  );
};

export default MapDesktopMarker;
